import React, { useState, useEffect, useCallback } from 'react';
import { investmentPerspectivesAPI } from '../../services/api';

/**
 * Choose which Portfolio Dashboard suite(s) each tailored client's Asset Class
 * Positioning / Overview tables average (e.g. the main suite, not the ETF
 * suite). Suites are listed exactly as the client's Dashboard tabs, so a ticked
 * suite is the same calculation as that tab. One choice per client for every
 * month; ticks are local until Save. A client with nothing ticked shows a
 * 'not set' message in its positioning blocks.
 */
const PositioningModal = ({ isOpen, onClose, isDrummondUser, onSaved }) => {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState({}); // domain -> Set (local, unsaved)
  const [baseline, setBaseline] = useState({}); // domain -> Set (last saved)
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setSaveState('idle');
    try {
      const res = await investmentPerspectivesAPI.getPositioningSuites();
      const list = res.clients || [];
      const sel = {};
      list.forEach((c) => { sel[c.domain] = new Set(c.selected || []); });
      setClients(list);
      setSelected(sel);
      setBaseline(Object.fromEntries(Object.entries(sel).map(([d, s]) => [d, new Set(s)])));
    } catch (err) {
      console.error('Error loading positioning suites:', err);
      setLoadError(err?.response?.data?.detail || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && isDrummondUser) load();
  }, [isOpen, isDrummondUser, load]);

  const toggle = (domain, label) => {
    setSelected((prev) => {
      const next = new Set(prev[domain] || []);
      if (next.has(label)) next.delete(label); else next.add(label);
      return { ...prev, [domain]: next };
    });
    setSaveState('idle');
  };

  const sameSet = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));
  const dirtyDomains = clients
    .map((c) => c.domain)
    .filter((d) => !sameSet(selected[d] || new Set(), baseline[d] || new Set()));

  const handleSave = async () => {
    setSaveState('saving');
    setSaveError(null);
    try {
      const updated = {};
      for (const d of dirtyDomains) {
        const res = await investmentPerspectivesAPI.setPositioningSuites(d, [...(selected[d] || [])]);
        updated[d] = res.client;
      }
      setClients((prev) => prev.map((c) => updated[c.domain] || c));
      setBaseline((prev) => {
        const next = { ...prev };
        Object.entries(updated).forEach(([d, c]) => { next[d] = new Set(c.selected || []); });
        return next;
      });
      setSaveState('saved');
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error saving positioning suites:', err);
      setSaveError(err?.response?.data?.detail || 'Save failed — try again');
      setSaveState('error');
    }
  };

  if (!isOpen) return null;

  const saveLabel = saveState === 'saving' ? 'Saving…'
    : saveState === 'saved' ? 'Saved ✓'
    : saveState === 'error' ? 'Retry Save'
    : 'Save';

  return (
    <div className="propagate-modal-overlay" onClick={onClose}>
      <div className="propagate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="propagate-modal-header">
          <h3>Manage Positioning</h3>
          <button className="propagate-modal-close" onClick={onClose}>&times;</button>
        </div>

        <p className="propagate-modal-desc">
          Tick the Portfolio Dashboard suite(s) each tailored client's Asset Class
          Positioning and Overview tables should average. A ticked suite gives the
          same result as that client's Dashboard tab. Applies to every month;
          a report a client has already opened stays as it was. Remember to press Save.
        </p>

        <div className="propagate-grid-wrapper">
          {loading ? (
            <div className="publish-loading">Loading clients…</div>
          ) : loadError ? (
            <div className="publish-loading">Could not load positioning suites: {loadError}</div>
          ) : (
            <table className="propagate-grid">
              <thead>
                <tr>
                  <th className="propagate-corner">Suite</th>
                  <th className="propagate-col-header">Portfolios</th>
                  <th className="propagate-col-header">Use</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => {
                  const sel = selected[c.domain] || new Set();
                  return (
                    <React.Fragment key={c.domain}>
                      <tr className="propagate-page-header">
                        <td colSpan={3}>
                          {c.company_name || c.domain} ({c.domain})
                          {sel.size === 0 && ' — not set'}
                        </td>
                      </tr>
                      {(c.stale || []).length > 0 && (
                        <tr>
                          <td colSpan={3} className="ip-wl-domain">
                            <span className="ip-wl-error">
                              Saved suite no longer on the Dashboard: {c.stale.join(', ')}
                            </span>
                          </td>
                        </tr>
                      )}
                      {c.suites.length === 0 && (
                        <tr>
                          <td colSpan={3} className="ip-wl-domain">No tailored suites on this client's Dashboard</td>
                        </tr>
                      )}
                      {c.suites.map((s) => (
                        <tr key={`${c.domain}|${s.label}`}>
                          <td className="propagate-row-label">{s.label}</td>
                          <td className="ip-wl-domain">{s.codes.join(', ')}</td>
                          <td className="propagate-cell">
                            <input
                              type="checkbox"
                              checked={sel.has(s.label)}
                              disabled={saveState === 'saving'}
                              onChange={() => toggle(c.domain, s.label)}
                            />
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="propagate-modal-footer">
          {saveState === 'error' && <span className="ip-wl-error">{saveError}</span>}
          <button className="btn-cancel" onClick={onClose}>Close</button>
          <button
            className="btn-publish"
            onClick={handleSave}
            disabled={loading || !!loadError || saveState === 'saving'
              || (dirtyDomains.length === 0 && saveState !== 'error')}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PositioningModal;
